import React, {
  forwardRef, useEffect, useState, useRef, useCallback
} from 'react';
import { number } from 'prop-types';
import {
  Popover, Typography, Button, Link, useBreakpoint
} from '@one-thd/sui-atomic-components';
import thdCustomer from '@thd-olt-global/thd-customer';
import { URLS } from '../utils';

const SignInBubble = forwardRef(({ time }, ref) => {

  const [open, togglePopover] = useState(false);
  const closeTimeoutIdRef = useRef(null);
  const scrollYRef = useRef(0);
  const { greater: isLargeScreen } = useBreakpoint('lg');

  const closePopover = () => togglePopover(false);

  const openPopover = () => togglePopover(true);

  const closeOnScroll = useCallback(() => {
    if (Math.abs(window.scrollY - scrollYRef.current) >= 20) {
      closePopover();
    }
  }, []);

  const shouldShowSignInReminder = () => {
    try {
      const executionTimestamp = Date.now();
      const bubbleTimestamp = localStorage.getItem('signInReminderTimestamp');
      const lastReminderTimestamp = Number(bubbleTimestamp);
      const THIRTY_MINUTES = 1800000;
      const TEN_SECONDS = 10000;

      if (!bubbleTimestamp && (!thdCustomer?.isLoggedIn || thdCustomer?.isPersisted)) {
        // There is no record of the SignIn Bubble Timestamp and the user is not logged in or the user is persisted.
        openPopover();
        localStorage.setItem('signInReminderTimestamp', executionTimestamp);
      } else if (((executionTimestamp - lastReminderTimestamp) < TEN_SECONDS)
        && (!thdCustomer?.isLoggedIn || thdCustomer?.isPersisted)) {
        // The reminder has been shown for less than 10 seconds
        openPopover();
        localStorage.setItem('signInReminderTimestamp', executionTimestamp);
      } else if (((executionTimestamp - lastReminderTimestamp) > THIRTY_MINUTES)
        && (!thdCustomer?.isLoggedIn || thdCustomer?.isPersisted)) {
        // The last time we have shown the reminder has exceed 30 minutes and the user is not logged in or KMSI
        openPopover();
        localStorage.setItem('signInReminderTimestamp', executionTimestamp);
      }
    } catch (error) {
      // console.log("Something went wrong trying to retrieve the timestamp")
    }
  };

  useEffect(() => {
    const shouldShowTimeoutId = setTimeout(shouldShowSignInReminder, 1000);
    scrollYRef.current = window.scrollY;
    if (!window.LIFE_CYCLE_EVENT_BUS) {
      // eslint-disable-next-line no-console
      console.error('life cycle event bus was not found, unable to setup trigger for Sign In Bubble');
    } else {
      window.LIFE_CYCLE_EVENT_BUS.trigger('ocm_pd_component_sign-in-bubble.ready');
    }
    return () => clearTimeout(shouldShowTimeoutId);
  }, []);

  useEffect(() => {
    if (open) {
      closeTimeoutIdRef.current = setTimeout(closePopover, time);
      window.addEventListener('scroll', closeOnScroll);
      return;
    }
    clearTimeout(closeTimeoutIdRef.current);
    window.removeEventListener('scroll', closeOnScroll);
  }, [open]);

  return (
    <Popover
      style={{ zIndex: 1000 }}
      open={open}
      disablePortal
      id="sign-in-bubble"
      placement={isLargeScreen ? 'bottom-end' : 'top-end'}
      anchorEl={ref.current}
      title="Sign in for the best experience"
      onClose={closePopover}
      data-testid="popover-component-signInTest"
      data-component="SignInBubble"
    >
      <section className="sui-mt-1 sui-mb-3">
        <Button
          href={URLS.SIGN_IN_BUBBLE}
          variant="primary"
          data-testid="button-component-signInTest"
          fullWidth
        >
          Sign In
        </Button>
      </section>

      <section className="sui-flex sui-gap-4 lg:sui-w-96">
        <Typography variant="body-xs">
          Don&apos;t have an account?
        </Typography>
        <Link
          variant="body-xs"
          weight="bold"
          data-testid="link-component-signInTest"
          href={URLS.CREATE_ACCOUNT_BUBBLE}
        >
          Create an Account
        </Link>
      </section>

    </Popover>
  );
});

SignInBubble.propTypes = {
  time: number
};

SignInBubble.defaultProps = {
  time: 10000
};

SignInBubble.displayName = 'SignInBubble';

export { SignInBubble };